import { gql, useLazyQuery } from '@apollo/client';
import { EMPTY_ARRAY } from '@modules/common/constants';
import dayjs from 'dayjs';
import { Chat, ListChatsInput, Query } from 'generated/graphql';
import { useMemo } from 'react';
import { useGetUser } from 'v2/core/services/user';

export const LIST_CHAT_GQL = gql`
  query listChats($tenantID: ID!, $userID: ID!, $responseSize: Int) {
    listChats(
      request: {
        tenantID: $tenantID
        userID: $userID
        responseSize: $responseSize
      }
    ) {
      chats {
        id
        viewToken
        editToken
        sessionID
        title
        createdAt
        updatedAt
        status
      }
    }
  }
`;

const RESPONSE_SIZE = 25;

export const useFetchChatList = () => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const [fetchChatList, { data, loading }] = useLazyQuery<
    Query,
    ListChatsInput
  >(LIST_CHAT_GQL, {
    variables: {
      tenantID,
      userID,
      responseSize: RESPONSE_SIZE,
    },
    fetchPolicy: 'cache-and-network',
  });

  const chatListWithRelativeTime = useMemo(() => {
    const chats = data?.listChats?.chats ?? EMPTY_ARRAY;
    const chatsWithRelativeTime: Record<string, Omit<Chat, 'responses'>[]> = {};

    chats.forEach((chat) => {
      let groupByName = '';
      const now = dayjs();
      const chatUpdatedAtDate = dayjs(chat.updatedAt * 1000);

      if (chatUpdatedAtDate.isSame(now, 'day')) {
        groupByName = 'Today';
      } else if (chatUpdatedAtDate.isAfter(now.subtract(7, 'days'))) {
        groupByName = 'Previous 7 Days';
      } else if (chatUpdatedAtDate.isAfter(now.subtract(30, 'days'))) {
        groupByName = 'Previous 30 Days';
      } else {
        groupByName = `${chatUpdatedAtDate.format(
          'MMMM',
        )}, ${chatUpdatedAtDate.get('year')}`;
      }

      chatsWithRelativeTime[groupByName] = [
        ...(chatsWithRelativeTime[groupByName] ?? []),
        chat,
      ];
    });

    return chatsWithRelativeTime;
  }, [data?.listChats?.chats]);

  const chatList = data?.listChats?.chats;

  return [
    fetchChatList,
    {
      chatListWithRelativeTime: chatListWithRelativeTime,
      chatList: chatList ?? EMPTY_ARRAY,
      isLoading: chatList ? false : loading,
    },
  ] as const;
};
